// Author: Çağrı Erdem, 2024
// Description: User interface for dB study2.

import { globalFetch } from './globalFetch.js';
import { showPopup_duringQ, showPopup_post, showPopup_pre } from './utils/embedQs.js';

//workers:
const tempoWorker = new Worker(new URL('./workers/tempoWorker.js', import.meta.url), { type: 'module' }); // for tempo estimation
const ajaxWorker = new Worker(new URL('./workers/workerAjax.js', import.meta.url),{type: 'module'}); // for sending data to the server


// UI Elements
const tempoSlider = document.getElementById('tempo');
const recIndicator = document.getElementById('recIndicator');
const arrayList = document.getElementById('arrayList');
const recLight = document.getElementById('tapLight');

// Variables
let beatsInput = 8; // 8 beats (2 bars)
let quantizeSelect = 4; // 1/16
let currentArray = [];
let recording = false;
let recordingStartedAt = 0;
let isHttpConnected = true; // we keep it true with the new UI
let tappedRhythms = [];
let lockBPM = false;
// 0.8 temp ve 1.47 thresh B sampling icin iyi baslangic
let autoReload = false; // Set to true to enable auto-reload after a certain duration
let tapEvents = [];
let firstTap = false;
let estimatedBPM = 90;

//-------------------------------------
//-------------------------------------
// User Study2 structure
//-------------------------------------
//-------------------------------------

export let deploy = true; // TODO: Set to true when deploying the study app
//pre-questionnaire
window.addEventListener('DOMContentLoaded', (event) => { 
    if (deploy) {
        setTimeout(function() {
            promptIDs();  // Prompt for IDs in deployment mode
        }, 750);
        
        if (currentSessionIndex === 0) {
            setTimeout(function() {
                showPopup_pre();
              }, 1500);
        }
    }
});

document.addEventListener('DOMContentLoaded', function() {
    const checkbox = document.getElementById('toggleCanvasTop');
    const canvas = document.getElementById('hiddenCanvasTop');

    // Set initial state based on the checkbox's checked state
    canvas.style.display = checkbox.checked ? 'block' : 'none';

    // Event listener for checkbox change
    checkbox.addEventListener('change', function() {
        canvas.style.display = this.checked ? 'block' : 'none';
    });
});



document.getElementById('toggleCanvasBottom').addEventListener('change', function() {
    const canvas = document.getElementById('hiddenCanvasBottom');
    canvas.style.display = this.checked ? 'block' : 'none';
});


////////////////////////////////////////
////////// ID management ///////
////////////////////////////////////////

// Global variables to store conductor ID and participant ID
export let participantID = "";
export let sessionID = "";
let previousSessionID = "";

// Function to prompt for conductor and participant IDs and display them in the UI
function promptIDs() {
    if (!participantID) {  // Only prompt in the first session (test session) or if there's no stored ID
        participantID = prompt("Please enter a Participant ID:");
        while (!participantID) {
            participantID = prompt("You must have a Participant ID, which will ensure the integrity of the recorded data and questionnaire responses:");
        }
        // Display Participant ID on the top-right corner
        displayIDs();
    }
}

// Function to display conductor and participant IDs in the UI
function displayIDs() {
    // Create or update Participant ID display
    let participantIDDisplay = document.getElementById("participantIDDisplay");
    if (!participantIDDisplay) {
        participantIDDisplay = document.createElement("div");
        participantIDDisplay.id = "participantIDDisplay";
        document.body.appendChild(participantIDDisplay);
    }
    participantIDDisplay.style.position = "absolute";
    participantIDDisplay.style.top = "40px";  // Positioned below Conductor ID
    participantIDDisplay.style.right = "10px";
    participantIDDisplay.style.padding = "5px";
    participantIDDisplay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    participantIDDisplay.style.color = "white";
    participantIDDisplay.textContent = `Participant ID: ${participantID}`;
}
////////////////////////////////////////
////////////////////////////////////////


// Set up an event listener for the first user interaction
function setupFirstInteractionListener() {
    const firstInteractionHandler = () => {
        if (deploy) {
            promptIDs();  // Prompt for IDs in deployment mode
        }
        // Remove event listeners once the IDs have been prompted
        document.removeEventListener("click", firstInteractionHandler);
        document.removeEventListener("keydown", firstInteractionHandler);
    };

    document.addEventListener("click", firstInteractionHandler);
    document.addEventListener("keydown", firstInteractionHandler);
}

// Call this function when the page loads or the study is reset
document.addEventListener("DOMContentLoaded", function() {
    initializeSessions();
    loadCurrentSession();
    setupFirstInteractionListener();  // Set up the first interaction listener

    setAutoReload(autoReloadDuration, autoReload);
});

// Reset conductor and participant IDs and clear their display when resetStudy is called
function resetStudyState() {
    localStorage.clear(); // Clear all localStorage data related to sessions
    currentSessionIndex = 0;
    participantID = ""; // Reset participant ID

    const participantIDDisplay = document.getElementById("participantIDDisplay");
    if (participantIDDisplay) {
        participantIDDisplay.remove();
    }

    // Reload the page to start fresh
    location.reload();
}


// Study conditions (generative parameters)
let temp = 0.1;
let thresh = 0.09;
const highTemp = 1.8;
const lowTemp = 0.25;
const highThresh = 0.135;
const lowThresh = 0.099;
const dBstudy2sessions = [ 
    { id: `Test`, tempo: estimatedBPM, temp: 1., threshold: 0.127 }, //mid values
    { id: `VOYAGER`, tempo: estimatedBPM, temp: highTemp, threshold: highThresh },
    { id: `ALICE`, tempo: estimatedBPM, temp: lowTemp, threshold: highThresh },
    { id: `ILLIAC`, tempo: estimatedBPM, temp: highTemp, threshold: lowThresh },
    { id: `CYSP`, tempo: estimatedBPM, temp: lowTemp, threshold: lowThresh }
] 


// State variables
let currentSessionIndex = 0;
let sessionOrder = [];
let autoReloadTimeout;
const autoReloadDuration = 3000; // 2 * 60 * 1000; // Default 2 minutes

// Initialize session categories and order
function initializeSessions() {
    const actualSessions = dBstudy2sessions.slice(1);
    shuffleSessions(actualSessions);
    // Create the session order: Test session followed by the actual sessions
    sessionOrder = [dBstudy2sessions[0], ...actualSessions];
}

// Shuffle the session order
function shuffleSessions(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}

// Apply session parameters to the UI
function applySession(session) {

    let sName = session.id.split(" ");
    document.getElementById('sessionName').textContent = `Session ID: ${sName[0]}`; // TODO: bu da shuffle ile gelsin
    document.getElementById('sessionProgress').textContent = `(${currentSessionIndex} / ${dBstudy2sessions.length - 1})`;

    if (currentSessionIndex === 0) {
        document.getElementById('sessionName').style.color = 'red';
        document.getElementById('sessionProgress').style.color = 'red';
    } else {
        document.getElementById('sessionName').style.color = 'green';
        document.getElementById('sessionProgress').style.color = 'green';
    }

    sessionID = session.id;
    // Find the corresponding session object
    const currentSession = dBstudy2sessions.find(s => s.id === sessionID);
    if (currentSession) {
        thresh = currentSession.threshold;
        temp = currentSession.temp;
    }
    // console.log(`Applied Session ID: ${session.id}, Tempo: ${session.tempo}, Temp: ${temp}, Thresh: ${thresh}`); //debug
}




// Clear session state when all sessions are completed or reset
function clearSessionState() {
    localStorage.removeItem('currentSessionIndex');
    currentSessionIndex = 0;
}


// Set up event listeners for buttons
document.getElementById('nextSessionBtn').addEventListener('click', nextSession);
document.getElementById('resetStudy').addEventListener('click', resetStudyState);



// Function to move to the next session
function nextSession() {
    currentSessionIndex++;

    if (currentSessionIndex < sessionOrder.length) {
        applySession(sessionOrder[currentSessionIndex]);
        localStorage.setItem('currentSessionIndex', currentSessionIndex);
    }

    if (deploy && currentSessionIndex >= 2 && currentSessionIndex <= 5) {
        showPopup_duringQ(); 
    }

    if (deploy && currentSessionIndex === 5) {
        // All sessions completed
        alert('All sessions are completed!');
        showPopup_post();
        // resetStudyState();
    }
}


// Load the current session based on stored state
function loadCurrentSession() {
    currentSessionIndex = parseInt(localStorage.getItem('currentSessionIndex')) || 0;

    // If index is out of bounds, reset to 0
    if (currentSessionIndex >= sessionOrder.length) {
        currentSessionIndex = 0;
    }

    applySession(sessionOrder[currentSessionIndex]);
}




function setAutoReload(duration, autoReload) {
    if (autoReload === true) {
        clearTimeout(autoReloadTimeout);
        autoReloadTimeout = setTimeout(function() {
            location.reload();
        }, duration);
    }
}



///////More (GUI) stuff
////
function hideControls() {
    //TODO: zaman icerisinde netlesecek burasi

    document.getElementById('tempo').style.display = 'none';
    document.getElementById('knob1Value').style.display = 'none';
    document.getElementById('metronomeVolume').style.display = 'none';
    document.getElementById('knob2Value').style.display = 'none';

    document.getElementById('toggleMetronome').style.display = 'none';
    // NOTE: comment out `startMetronome();` in playback.js

    // document.getElementById('temp').style.display = 'none';
    // document.getElementById('knob3Value').style.display = 'none';
    // document.getElementById('tolerance').style.display = 'none';
    // document.getElementById('knob4Value').style.display = 'none';

    document.getElementById('changeButton').style.display = 'none';
    document.getElementById('download-btn').style.display = 'none';

    document.getElementById('helpToggle').style.display = 'none';
    document.getElementById('helpToggleText').style.display = 'none';

    //TODO: togglePlaybackButton should also be enabled by default and hidden
    
    document.getElementById('themeToggleCheckbox').style.display = 'none';
    document.getElementById('themeToggleText').style.display = 'none';
    document.getElementById('themeSwitchSwitch').style.display = 'none';
    document.body.classList.add('dark-theme');
    themeToggleCheckbox.checked = true; // set to dark

    // face-pose-track related:
    document.getElementById('blendShapesDisplay').style.visibility = 'hidden';
    // document.getElementById('bsColumn1').style.display = 'none'; //TODO: bunlari disable edince her sey siciyo. bi toparla burayi
    // document.getElementById('bsColumn2').style.display = 'none';
    // document.getElementById('poseColumn').style.display = 'none';
}
hideControls();

//-------------------------------------
// Functions
//-------------------------------------

async function initializeApp() {
    // Set default BPM or retrieve it from a saved setting or slider
    const defaultBPM = tempoSlider.value;

    console.log("App initialized!");
    // console.log("Default BPM:", defaultBPM);
}


/// UTILS ///

resetButton.addEventListener('click', () => {
    arrayList.innerHTML = '';
});


// Initialize the app
initializeApp();



///////////////////////
/////// tap2arr ///////
///////////////////////

// Listen to messages from the worker:
ajaxWorker.addEventListener('message', function(event) {
    const data = event.data;
    console.log("Setting MIDI data, length:", data.length);

    switch (data.type) {
        case 'arrayProcessed':
            // process the MIDI data as needed
            globalFetch.setMidiData(data.data);
            resetTappedRhythms();
            break;

        case 'error':
            console.error("Error from worker:", data.error);
            showNotification("Ooops! I messed it up." + "\n" + "Modify some parameters a bit or just refresh the page to try again.");
            break;
    }
});


function arrayToBinaryString(array) {
    return array.map(value => (value !== 0 ? '1' : '0')).join('');
}

function storeAndDisplayArray(array) {
    const li = document.createElement('li');
    li.textContent = arrayToBinaryString(array);
        // If the list has any items
        if (arrayList.firstChild) {
        // Insert 'li' before the first item, to the top of the list
        arrayList.insertBefore(li, arrayList.firstChild);
        } else {
        // If the list is empty, just append the 'li' as usual
        arrayList.appendChild(li);
    }
}

// Modify `sendArrayToServer` to post data to the worker:
function sendArrayToServer(array, BPM) {
    // console.log("Sending data to server:", array, "Tempo: ", BPM, "Temp: ", temp, "Thresh: ", thresh, "Session ID: ", sessionID);

    ajaxWorker.postMessage({
        cmd: 'sendArray',
        array: array,
        bpm: BPM,
        temperatureValue: temp, //parseFloat(document.getElementById('temperature').value),
        hitTolerance: thresh, //parseFloat(document.getElementById('tolerance').value),
        isHttpConnected: isHttpConnected,  // we keep it true with the new UI     
        samplingStrategy: 'softmax_temp' //samplingStrategy['strategy'][samplingStrategyIndex]
    });
}



function resetTappedRhythms() {
  // tappedRhythms = [];
  tappedRhythms.length = 0;  // This empties the array without re-declaring it
}



//-------------------------------------
// Event Listeners
//-------------------------------------

function resetRecordingState() {
    tapEvents = [];
    recordingStartedAt = null;
    firstTap = false;
    recording = false;
    tempoWorker.postMessage({ cmd: 'reset' }); // Reset the tempoWorker state
}



document.addEventListener('keydown', (e) => {
    if (e.key === 'a') {
        // Toggle recording
        if (!recording) {
            startRecording();
            e.preventDefault(); 
        } else {
            stopRecording();
        }
    }
});


// Function to start recording
function startRecording() {
    resetRecordingState();  // Reset the state before recording
    recordingStartedAt = Date.now();  // Set the starting point of the recording
    recIndicator.innerText = 'ON AIR!';  // Display "ON AIR"
    recIndicator.classList.add('recording');
    recording = true;
    
    recLight.classList.add('light-on');
    console.log("Recording started...");
}

// Function to stop recording
function stopRecording() {
    recording = false;
    recIndicator.innerText = '';  // Clear the "ON AIR" display
    recIndicator.classList.remove('recording');
    recLight.classList.remove('light-on');
    recLight.classList.add('light-off');
    console.log("Recording completed!");

    // If no taps were recorded, send an array of zeros
    if (tapEvents.length === 0) {
        const emptyArray = new Array(32).fill(0);
        console.log("No taps recorded. Sending an array of 0s:", emptyArray);
        sendArrayToServer(emptyArray, estimatedBPM);

    } else {
        // Process the recorded taps
        processTaps(tapEvents);
    }
    resetRecordingState();
}


// Function to record taps (spacebar key press)
document.addEventListener('keydown', (e) => {
    if (e.key === ' ' && recording) {
        e.preventDefault(); //To prevent the browser from scrolling when the spacebar is pressed
        recordTapEvent();  // Record the tap
    }
});

// Function to record individual taps
function recordTapEvent() {
    const elapsedMs = Date.now() - recordingStartedAt;

    if (!firstTap) {
        recordingStartedAt = Date.now();  // Set the first tap as point 0
        tapEvents.push(0);  // First tap is always at time 0
        firstTap = true;
    } else {
        tapEvents.push(elapsedMs);  // Store the time difference from the first tap
    }

    console.log(`Tap recorded: ${elapsedMs} ms`);
}




function processTaps(tapEvents) {
    // Send tap events to worker for tempo estimation
    tempoWorker.postMessage({
        cmd: 'calculateTempo',
        tapTimes: tapEvents
    });

    currentArray = quantizeTaps(tapEvents, estimatedBPM); // Quantize the taps into a fixed-length array
    storeAndDisplayArray(currentArray);
}

tempoWorker.addEventListener('message', function (event) {
    const data = event.data;

    if (data.type === 'tempoCalculated') {
        let bpm = parseFloat(data.advancedTempo || data.tempo);

        // Adjust tempo: double time if < 50 BPM, half time if > 200 BPM
        if (bpm < 40) {
            bpm *= 2;  // Double time
            // console.log(`Tempo too slow, adjusted to double time: ${Math.round(bpm)} BPM`);
        } else if (bpm > 150) {
            bpm /= 2;  // Half time
            // console.log(`Tempo too fast, adjusted to half time: ${Math.round(bpm)} BPM`);
        }

        estimatedBPM = bpm; // clampBPM(bpm);  // Ensure tempo stays within limits
        console.log(`Final estimated tempo: ${Math.round(estimatedBPM)} BPM`);

        let tempo2model = parseFloat(estimatedBPM); // Update the global tempo variables across other modules
        sendArrayToServer(currentArray, tempo2model);
    }
});


export function getBpm() {
    let temp;
    temp = estimatedBPM;
    // console.log("Current BPM:", temp);
    return temp;
}

const testBtn = document.getElementById('testBtn');

testBtn.addEventListener('click', () => {
    lockBPM = !lockBPM;
    console.log("Lock BPM:", lockBPM);

    if (lockBPM) {
        testBtn.textContent = 'Unlock the Tempo';
    } else {
        testBtn.textContent = 'Lock the Tempo';
    }
});



// Function to quantize the taps into a fixed-length array
function quantizeTaps(tapEvents, bpm) {
    const quantizeValue = parseInt(quantizeSelect);
    const totalSteps = parseInt(beatsInput) * parseInt(quantizeSelect);  // Fixed length of the array
    const stepDuration = (60 * 1000) / (bpm * quantizeValue);  // Step duration (16th notes)

    const quantizedArray = new Array(totalSteps).fill(0);
    for (let tap of tapEvents) {
        const stepIndex = Math.floor(tap / stepDuration);
        if (stepIndex < totalSteps) {
            quantizedArray[stepIndex] = 1;  // Mark the step as a tap
        }
    }

    return quantizedArray;
}



//tempo estimation related
function removeOutliers(intervals) {
    const sortedIntervals = [...intervals].sort((a, b) => a - b);
    const q1 = sortedIntervals[Math.floor(sortedIntervals.length / 4)];
    const q3 = sortedIntervals[Math.ceil((sortedIntervals.length * 3) / 4)];
    const iqr = q3 - q1;  // Interquartile range

    // Filter out intervals that are too far outside the interquartile range (outliers)
    return intervals.filter(interval => interval >= (q1 - 1.5 * iqr) && interval <= (q3 + 1.5 * iqr));
}

function weightedAverage(intervals) {
    const totalWeight = intervals.reduce((acc, _, index) => acc + (index + 1), 0);  // Sum of weights
    const weightedSum = intervals.reduce((acc, interval, index) => acc + interval * (index + 1), 0);  // Weighted sum
    return weightedSum / totalWeight;
}

function ignoreEarlyTaps(intervals) {
    return intervals.slice(2);  // Ignore the first two intervals
}


function calculateMedian(intervals) {
    const sorted = [...intervals].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function windowedAverage(intervals, windowSize = 4) {
    const recentIntervals = intervals.slice(-windowSize);  // Take the most recent n intervals
    return recentIntervals.reduce((a, b) => a + b) / recentIntervals.length;  // Average them
}

function clampBPM(bpm, minBPM = 60, maxBPM = 180) {
    return Math.max(minBPM, Math.min(bpm, maxBPM));
}


export function metroTrigger() {
    return true;
  }


// Export the tapped array for other modules to use
export function getTappedRhythms() {
    return currentArray;
}



///////////
///HELP///
///////////
// Functions to show notifications and help instructions

// Uyari sistemi
function showNotification(message) {
    const container = document.getElementById('notificationContainer');
    const notification = document.createElement('div');
    notification.classList.add('error_notification'); // Add a class to style the notification
    notification.textContent = message;

    container.appendChild(notification);

    setTimeout(() => {
        notification.remove();
    }, 3000); // Remove the notification after 3 seconds
}



// A map linking GUI element IDs to notification IDs
const helpMap = {
    'db_app': 'notificationForApp', 
    'db_app2': 'notificationForApp2',
    'playbackSubContainer': 'notificationForplaybackSubContainer',
    'tempo': 'notificationForTempo',
    'metronomeVolume': 'notificationForVolume',
    'tapTempoLight': 'notificationForTapTempoLight',
    'metroFeedback': 'notificationForMetronomeToggle',
    'temp': 'notificationForTempKnob',
    'tolerance': 'notificationForToleranceKnob',
    'invisibleAnchor_pRoll': 'notificationForPianoRoll',
    'changeButton': 'notificationForChangeBtn',
    'togglePlaybackButton': 'notificationForTogglePlaybackBtn',
    'tapLight': 'notificationForTapLight',
    'recIndicator': 'notificationForRecIndicator',
    'download-btn': 'notificationForDownloadBtn',
    'helpToggle': 'notificationForFinal',
};

let currentStep = 0;
const helpEntries = Object.entries(helpMap);

document.getElementById('helpToggle').addEventListener('change', function(event) {
    if (event.target.checked) {
        currentStep = 0; // Start from the first help item
        populateHelpDots();
        showHelpStep(currentStep);
    } else {
        document.getElementById('helpOverlay').style.display = 'none';
    }
});

function populateHelpDots() {
    const dotsContainer = document.getElementById('helpDotsContainer');
    dotsContainer.innerHTML = ''; // Clear existing dots

    helpEntries.forEach((_, index) => {
        const dot = document.createElement('span');
        dot.classList.add('help-dot');
        if (index === currentStep) {
            dot.classList.add('active'); // Mark the current step's dot as active
        }
        dot.addEventListener('click', () => {
            currentStep = index; // Update currentStep to the clicked dot's index
            showHelpStep(currentStep);
        });
        dotsContainer.appendChild(dot);
    });
}


document.addEventListener('keydown', function(event) {
    if (event.key === 'ArrowLeft') {
        // Simulate clicking the "Prev" button
        document.getElementById('prevHelp').click();
    } else if (event.key === 'ArrowRight') {
        // Simulate clicking the "Next" button
        document.getElementById('nextHelp').click();
    } else if (event.key === 'Escape') {
        // Simulate clicking the "Close" button
        document.getElementById('closeHelp').click();
    }
});



// theme tests
document.getElementById('themeToggleCheckbox').addEventListener('change', function() {
    if (this.checked) {
        // Apply dark theme
        document.body.classList.add('dark-theme');
    } else {
        // Revert to light theme
        document.body.classList.remove('dark-theme');
    }
});




